import { onMounted } from 'vue'
import { onLCP, onTTFB } from 'web-vitals'
import { onCLS, onINP } from 'web-vitals/attribution'

import { useLoggerStore } from '@/store/logger'
import { getElementXPath } from '@/utils/get-element-xpath'

function getViewportString(): string {
    const width = Math.round(window.visualViewport?.width ?? window.innerWidth)
    const height = Math.round(window.visualViewport?.height ?? window.innerHeight)
    const dpr = window.devicePixelRatio || 1
    const dprStr = Number.isFinite(dpr) ? dpr.toFixed(2).replace(/\.00$/, '') : '1'
    return `${width}x${height}@${dprStr}`
}

function round(value: number): number {
    return Math.round(value)
}

function getInpTiming(inputDelay: number, processingDuration: number, presentationDelay: number): string {
    return `input:${round(inputDelay)},processing:${round(processingDuration)},presentation:${round(presentationDelay)}`
}

function getRumTarget(node: Node | null): string {
    if (!node) {
        return 'node:null'
    }

    if (!(node instanceof Element)) {
        return `node:type-${node.nodeType}`
    }

    const targetElement = node.closest<HTMLElement>('[data-interface-target]')
    const target = targetElement?.dataset.interfaceTarget
    if (!target) {
        const tag = node.tagName.toLowerCase()
        return node.id ? `element:${tag}#${node.id}` : `element:${tag}`
    }

    const section = targetElement.closest<HTMLElement>('[data-interface-section]')?.dataset.interfaceSection
    return section ? `interface:${section}/${target}` : `interface:${target}`
}

function getInpDiagnostics(
    metric: Parameters<typeof onINP>[0] extends (metric: infer T) => unknown ? T : never,
): string {
    if (metric.value < 200) {
        return ''
    }

    const { attribution } = metric
    const { longestScript } = attribution
    const events = [...new Set(attribution.processedEventEntries.map((entry) => entry.name))]
    const diagnostics = [
        `interaction:${attribution.interactionType}`,
        `interaction_at:${round(performance.timeOrigin + attribution.interactionTime)}`,
        `next_paint_at:${round(performance.timeOrigin + attribution.nextPaintTime)}`,
        `load:${attribution.loadState}`,
        `events:${events.join('|') || 'unknown'}`,
    ]

    if (attribution.totalScriptDuration !== undefined) {
        diagnostics.push(`script_total:${round(attribution.totalScriptDuration)}`)
    }

    if (attribution.totalStyleAndLayoutDuration !== undefined) {
        diagnostics.push(`style:${round(attribution.totalStyleAndLayoutDuration)}`)
    }

    if (attribution.totalPaintDuration !== undefined) {
        diagnostics.push(`paint:${round(attribution.totalPaintDuration)}`)
    }

    if (attribution.totalUnattributedDuration !== undefined) {
        diagnostics.push(`unattributed:${round(attribution.totalUnattributedDuration)}`)
    }

    if (longestScript) {
        const { forcedStyleAndLayoutDuration, invokerType, sourceFunctionName, sourceURL } = longestScript.entry

        diagnostics.push(
            `script:${sourceURL.split(/[?#]/, 1)[0]}`,
            `script_duration:${round(longestScript.intersectingDuration)}`,
            `script_subpart:${longestScript.subpart}`,
            `script_invoker:${invokerType}`,
            `script_function:${sourceFunctionName || 'anonymous'}`,
            `script_forced_style:${round(forcedStyleAndLayoutDuration)}`,
        )
    }

    return diagnostics.join(',')
}

export function useBrowserRumTracking(): void {
    const loggerStore = useLoggerStore()

    onMounted(() => {
        onCLS((metric) => {
            loggerStore.logEvent(
                {
                    event: 'custom_event',
                    eventName: 'page_cls',
                    dimension1: metric.value.toFixed(4),
                    dimension2: getViewportString(),
                    dimension3: metric.attribution.largestShiftTarget,
                },
                { flushImmediately: true },
            )
        })

        onINP(
            (metric) => {
                const timings = getInpTiming(
                    metric.attribution.inputDelay,
                    metric.attribution.processingDuration,
                    metric.attribution.presentationDelay,
                )
                const diagnostics = getInpDiagnostics(metric)

                loggerStore.logEvent(
                    {
                        event: 'custom_event',
                        eventName: 'page_inp',
                        dimension1: String(Math.round(metric.value)),
                        dimension2: metric.attribution.interactionTarget || 'node:null',
                        dimension3: diagnostics ? `${timings},${diagnostics}` : timings,
                    },
                    { flushImmediately: true },
                )
            },
            { generateTarget: getRumTarget },
        )

        onTTFB((metric) => {
            loggerStore.logEvent({
                event: 'custom_event',
                eventName: 'page_ttfb',
                dimension1: String(Math.round(metric.value)),
            })
        })

        onLCP((metric) => {
            loggerStore.logEvent(
                {
                    event: 'custom_event',
                    eventName: 'page_lcp',
                    dimension1: String(Math.round(metric.value)),
                    dimension2: getElementXPath(metric.entries.at(-1)?.element),
                },
                { flushImmediately: true },
            )
        })
    })
}
